/**
 * Frontend service for managing departments
 */

import { apiClient } from "@/services/api";
import { logError } from "@/lib/error-handling";
import { DepartmentDto } from "@/types/department-schema";
import { UpdateDepartmentInput } from "@/schemas";

export interface CreateDepartmentData {
  name: string;
  description?: string;
}

/**
 * Fetches all departments for the current authenticated organization.
 *
 * @returns A list of departments with their associated users.
 * @throws An error if the request fails.
 */
export async function fetchDepartments(): Promise<DepartmentDto[]> {
  try {
    return await apiClient<DepartmentDto[]>(`/api/admin/departments`, {
      method: "GET",
      cache: "no-store",
    });
  } catch (error) {
    logError(error, "fetchDepartments");
    throw error;
  }
}

/**
 * Creates a new department for the current authenticated organization.
 *
 * @param data - Department creation data
 * @returns The created department
 * @throws An error if the request fails.
 */
export async function createDepartment(
  data: CreateDepartmentData
): Promise<DepartmentDto> {
  try {
    const url = new URL("/api/admin/departments", window.location.origin);

    return await apiClient<DepartmentDto>(url.toString(), {
      method: "POST",
      body: JSON.stringify(data),
      cache: "no-store",
    });
  } catch (error) {
    logError(error, "createDepartment");
    throw error;
  }
}

/**
 * Deletes a department by its ID.
 *
 * @param departmentId - The ID of the department to delete
 * @returns A promise that resolves when the department is successfully deleted
 * @throws An error if the request fails or if the department cannot be deleted
 */
export async function deleteDepartment(
  departmentId: string
): Promise<{ message: string }> {
  try {
    const url = new URL(
      `/api/admin/departments/${departmentId}`,
      window.location.origin
    );

    return await apiClient<{ message: string }>(url.toString(), {
      method: "DELETE",
    });
  } catch (error) {
    logError(error, "deleteDepartment");
    throw error;
  }
}

/**
 * Updates a department by its ID.
 *
 * @param departmentId - The ID of the department to update
 * @param data - The data to update the department with
 * @returns The updated department
 * @throws An error if the request fails.
 */
export async function updateDepartment(
  departmentId: string,
  data: UpdateDepartmentInput
): Promise<DepartmentDto> {
  try {
    return await apiClient<DepartmentDto>(
      `/api/admin/departments/${departmentId}`,
      {
        method: "PATCH",
        body: JSON.stringify(data),
      }
    );
  } catch (error) {
    logError(error, "updateDepartment");
    throw error;
  }
}
